import { defineStore } from 'pinia'

const STORAGE_KEY = 'analysesFilters'

interface IAnalysesFilters {
  analysisIDs: string[]
  tierIDs: string[]
  locationIDs: string[]
  insuranceCustomerAccountIDs: string[]
  hideUnregistered: boolean
  hideCompleted: boolean
}

const DEFAULT_FILTERS: IAnalysesFilters = {
  analysisIDs: [],
  tierIDs: [],
  locationIDs: [],
  insuranceCustomerAccountIDs: [],
  hideUnregistered: false,
  hideCompleted: false,
}

export const useAnalysesFilterStore = defineStore('analysesFilter', {
  state: () => ({
    analysisIDs: [] as string[],
    tierIDs: [] as string[],
    locationIDs: [] as string[],
    insuranceCustomerAccountIDs: [] as string[],
    hideUnregistered: false,
    hideCompleted: false,
    isLoaded: false,
  }),

  getters: {
    hasActiveFilters(state): boolean {
      return (
        state.analysisIDs.length > 0 ||
        state.tierIDs.length > 0 ||
        state.locationIDs.length > 0 ||
        state.insuranceCustomerAccountIDs.length > 0 ||
        state.hideUnregistered ||
        state.hideCompleted
      )
    },
  },

  actions: {
    loadFilters() {
      if (this.isLoaded) return
      this.isLoaded = true

      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
          const parsed: unknown = JSON.parse(stored)
          if (parsed && typeof parsed === 'object') {
            const {
              analysisIDs,
              tierIDs,
              locationIDs,
              insuranceCustomerAccountIDs,
              hideUnregistered,
              hideCompleted,
            } = parsed as Partial<IAnalysesFilters>
            this.analysisIDs = Array.isArray(analysisIDs)
              ? analysisIDs.filter((x): x is string => typeof x === 'string')
              : []
            this.tierIDs = Array.isArray(tierIDs)
              ? tierIDs.filter((x): x is string => typeof x === 'string')
              : []
            this.locationIDs = Array.isArray(locationIDs)
              ? locationIDs.filter((x): x is string => typeof x === 'string')
              : []
            // Filters saved before Partner was renamed (MIL-402) carry the list as `partnerIDs`.
            const storedInsuranceCustomerAccountIDs: unknown =
              insuranceCustomerAccountIDs ?? (parsed as { partnerIDs?: unknown }).partnerIDs
            this.insuranceCustomerAccountIDs = Array.isArray(storedInsuranceCustomerAccountIDs)
              ? storedInsuranceCustomerAccountIDs.filter((x): x is string => typeof x === 'string')
              : []
            this.hideUnregistered = !!hideUnregistered
            this.hideCompleted = !!hideCompleted
            return
          }
        }
      } catch {}

      this.applyDefaults()
    },

    persist() {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          analysisIDs: this.analysisIDs,
          tierIDs: this.tierIDs,
          locationIDs: this.locationIDs,
          insuranceCustomerAccountIDs: this.insuranceCustomerAccountIDs,
          hideUnregistered: this.hideUnregistered,
          hideCompleted: this.hideCompleted,
        } satisfies IAnalysesFilters)
      )
    },

    setAnalysisIDs(ids: string[]) {
      this.analysisIDs = ids
      this.persist()
    },

    setTierIDs(ids: string[]) {
      this.tierIDs = ids
      this.persist()
    },

    setLocationIDs(ids: string[]) {
      this.locationIDs = ids
      this.persist()
    },

    setInsuranceCustomerAccountIDs(ids: string[]) {
      this.insuranceCustomerAccountIDs = ids
      this.persist()
    },

    setHideUnregistered(value: boolean) {
      this.hideUnregistered = value
      this.persist()
    },

    setHideCompleted(value: boolean) {
      this.hideCompleted = value
      this.persist()
    },

    applyDefaults() {
      this.analysisIDs = [...DEFAULT_FILTERS.analysisIDs]
      this.tierIDs = [...DEFAULT_FILTERS.tierIDs]
      this.locationIDs = [...DEFAULT_FILTERS.locationIDs]
      this.insuranceCustomerAccountIDs = [...DEFAULT_FILTERS.insuranceCustomerAccountIDs]
      this.hideUnregistered = DEFAULT_FILTERS.hideUnregistered
      this.hideCompleted = DEFAULT_FILTERS.hideCompleted
    },

    clearFilters() {
      this.applyDefaults()
      this.persist()
    },
  },
})
